import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { Reveal, scrollToId } from "./Reveal";
import { SYMPTOMS } from "./data";

const verdict = (n) => {
  if (n === 0) return { t: "Tocca i sintomi che riconosci.", s: "Nessun giudizio: è solo un modo per capire insieme da dove partire." };
  if (n <= 2) return { t: "Vale la pena un controllo.", s: "Anche un solo segnale, se persiste, merita uno sguardo attento alle gengive." };
  return { t: "Ti consiglio una visita parodontale.", s: "Prima si interviene, più denti naturali si riescono a salvare. Con calma, un passo alla volta." };
};

const SymptomChecker = ({ symptoms, setSymptoms }) => {
  const toggle = (s) => setSymptoms((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  const v = verdict(symptoms.length);
  return (
    <div className="rounded-[2rem] bg-[#FCFBF7] border border-[#1B3B28]/10 p-7 sm:p-10 shadow-[0_30px_80px_-40px_rgba(27,59,40,0.35)]" data-testid="symptom-checker">
      <div className="flex items-baseline justify-between">
        <p className="eyebrow text-[#526356]">Autovalutazione</p>
        <p className="f-mono text-xs text-[#3E6B48]" data-testid="symptom-count">{String(symptoms.length).padStart(2, "0")} / {SYMPTOMS.length}</p>
      </div>
      <div className="mt-6 flex flex-wrap gap-2.5">
        {SYMPTOMS.map((s, i) => {
          const on = symptoms.includes(s);
          return (
            <motion.button
              key={s}
              whileTap={{ scale: 0.96 }}
              onClick={() => toggle(s)}
              data-testid={`symptom-chip-${i}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm border transition-colors duration-300 ${
                on ? "bg-[#1B3B28] text-[#FCFBF7] border-[#1B3B28]" : "bg-transparent text-[#1E2922] border-[#1B3B28]/15 hover:border-[#3E6B48]"
              }`}
            >
              <span className={`grid place-items-center w-4 h-4 rounded-full ${on ? "bg-[#86C89B] text-[#0F2317]" : "border border-[#1B3B28]/30"}`}>
                {on && <Check size={10} strokeWidth={3} />}
              </span>
              {s}
            </motion.button>
          );
        })}
      </div>
      <div className="mt-8 pt-6 border-t border-[#1B3B28]/10 min-h-[120px]">
        <AnimatePresence mode="wait">
          <motion.div key={v.t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}>
            <p className="f-serif text-3xl text-[#1B3B28]" data-testid="symptom-verdict">{v.t}</p>
            <p className="text-sm text-[#526356] mt-2">{v.s}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <button
        onClick={() => scrollToId("prenota")}
        disabled={!symptoms.length}
        data-testid="symptom-to-booking-btn"
        className="mt-4 group inline-flex items-center gap-2 text-sm font-medium text-[#1B3B28] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Porta questi sintomi alla visita
        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </div>
  );
};

export const Periodontitis = ({ symptoms, setSymptoms }) => (
  <section id="parodontite" className="relative py-28 sm:py-40 bg-[#FCFBF7]" data-testid="periodontitis-section">
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10 grid lg:grid-cols-12 gap-16">
      <div className="lg:col-span-5">
        <SectionHead
          eyebrow="Che cos'è la parodontite"
          lines={["Un'infezione", "silenziosa,", "che si può fermare."]}
          intro="La parodontite — detta anche piorrea — è un'infezione batterica dei tessuti che sostengono i denti: gengiva, legamento e osso. I batteri si annidano nelle tasche gengivali e, se trascurati, possono portare alla mobilità e alla perdita dei denti."
        />
        <Reveal delay={0.3}>
          <p className="mt-6 max-w-xl text-[#526356] leading-relaxed">
            Non riguarda solo la bocca: l'infiammazione cronica dialoga con tutto l'organismo. Per questo va trattata per quello che è,
            <em className="f-serif text-xl text-[#1B3B28]"> un'infezione</em> — e la guarigione va verificata con dati oggettivi.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7 lg:pt-16">
        <Reveal delay={0.15}>
          <SymptomChecker symptoms={symptoms} setSymptoms={setSymptoms} />
        </Reveal>
        <Reveal delay={0.25}>
          <Link to="/test-gengive" data-testid="quiz-teaser-link"
            className="group mt-5 flex items-center justify-between gap-4 rounded-[1.5rem] bg-[#1B3B28] text-[#FCFBF7] px-7 py-6 hover:bg-[#3E6B48] transition-colors">
            <span><span className="eyebrow text-[#86C89B] block">Test gratuito · 1 minuto</span><span className="f-serif text-2xl sm:text-3xl block mt-1">Le tue gengive sono a rischio?</span></span>
            <ArrowRight size={20} className="shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);
